import { useEffect, useRef } from "react";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { Container } from "react-bootstrap";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import SearchComboBox, {
  type ComboBoxOption,
} from "@/components/shared/forms/SearchComboBox";
import { SCLR, SCLR_TEMPLATE } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { FaChalkboardTeacher } from "react-icons/fa";

export default function SCLRAddEditForm({
  staffClassroom,
  form,
  sclrDefaults,
  staffOptions = [],
  classroomOptions = [],
}: {
  staffClassroom: SCLR | undefined;
  form: UseFormReturn<SCLR, any, SCLR>;
  sclrDefaults?: Partial<SCLR>;
  staffOptions?: ComboBoxOption[];
  classroomOptions?: ComboBoxOption[];
}) {
  const params = useParams({
    shouldThrow: false,
    from: "/$leaid/_error-wrapper/records/staff/$staffId",
  });

  const newStaffClassroomTemplate: SCLR = {
    ...SCLR_TEMPLATE,
    ...(sclrDefaults || {}),
  };

  const classroom = staffClassroom ?? newStaffClassroomTemplate;
  const isEditing = !!staffClassroom;

  const leaContext = useRouteContext({ from: "/$leaid" });
  const coreData = useCoreData();
  const viewOnly = !leaContext.hasRole("SCLREdit");
  const tooltipMap = leaContext.coreData.tooltipMap.get("SCLR");

  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = form;

  const programYear = useWatch({ control, name: "ProgramYear" });
  const preschoolCode = useWatch({ control, name: "PreschoolCode" });
  const prevPreschoolRef = useRef(preschoolCode);

  const isStaffFieldDisabled =
    viewOnly ||
    (isEditing && !!staffClassroom?.LocalStaffId) ||
    !!sclrDefaults?.LocalStaffId;

  const isClassroomFieldDisabled =
    viewOnly ||
    (isEditing && !!staffClassroom?.LocalClassroomId) ||
    !!sclrDefaults?.LocalClassroomId;

  const isPreschoolLocked =
    (isEditing && !!staffClassroom?.PreschoolCode) ||
    (!!sclrDefaults?.PreschoolCode && !!sclrDefaults?.LocalClassroomId);

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(programYear)
    .map((x) => {
      return {
        Text: x.PreschoolName,
        Value: x.PreschoolCDSCode,
      };
    });

  useEffect(() => {
    if (prevPreschoolRef.current !== preschoolCode) {
      prevPreschoolRef.current = preschoolCode;
      if (!isClassroomFieldDisabled) {
        setValue("LocalClassroomId", "", { shouldValidate: false });
      }
    }
  }, [preschoolCode, setValue, isClassroomFieldDisabled]);

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div
        className="form-card-modern mb-3"
        style={
          (!isStaffFieldDisabled && staffOptions.length > 0) ||
          (!isClassroomFieldDisabled && classroomOptions.length > 0)
            ? { paddingBottom: 180 }
            : undefined
        }
      >
        <div className="section-header">
          <FaChalkboardTeacher className="section-icon" />
          <h5 className="section-title">Classroom Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                isEditing && !!staffClassroom.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(id, { required: "Program Year is required" })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                isPreschoolLocked && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Preschool is required" }}
                    options={preschoolOptions}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(id, { required: "Preschool is required" })}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>
        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="LocalClassroomId"
              labelText="Classroom ID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) =>
                isClassroomFieldDisabled ? (
                  <TextInput
                    id={id}
                    disabled={viewOnly}
                    readOnly
                    maxLength={50}
                    {...register(id, { required: "Classroom ID is required" })}
                  />
                ) : classroomOptions.length > 0 ? (
                  <Controller
                    name="LocalClassroomId"
                    control={control}
                    rules={{ required: "Classroom ID is required" }}
                    render={({ field }) => (
                      <SearchComboBox
                        id={id}
                        options={classroomOptions}
                        value={field.value}
                        placeholder="Search by classroom name or ID"
                        noMatchMessage="No matching classrooms found"
                        hasError={!!errors.LocalClassroomId}
                        isRequired
                        ariaDescribedBy={`${id}-description`}
                        onChange={(value) => {
                          setValue("LocalClassroomId", value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                      />
                    )}
                  />
                ) : (
                  <TextInput
                    id={id}
                    disabled={false}
                    maxLength={50}
                    {...register(id, { required: "Classroom ID is required" })}
                  />
                )
              }
            </FormFieldRow>
          </div>
          <div className="col-md-6">
            <FormFieldRow
              name="LocalStaffId"
              labelText="Staff Local ID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id) =>
                isStaffFieldDisabled ? (
                  <TextInput
                    id={id}
                    disabled={viewOnly}
                    readOnly
                    maxLength={50}
                    {...register(id, {
                      required: "Staff Local ID is required",
                    })}
                  />
                ) : staffOptions.length > 0 ? (
                  <Controller
                    name="LocalStaffId"
                    control={control}
                    rules={{ required: "Staff Local ID is required" }}
                    render={({ field }) => (
                      <SearchComboBox
                        id={id}
                        options={staffOptions}
                        value={field.value}
                        placeholder="Search by staff name or ID"
                        noMatchMessage="No matching staff found"
                        hasError={!!errors.LocalStaffId}
                        onChange={(value) => {
                          setValue("LocalStaffId", value, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                      />
                    )}
                  />
                ) : (
                  <TextInput
                    id={id}
                    maxLength={50}
                    {...register(id, {
                      required: "Staff Local ID is required",
                    })}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>
        <span
          id="spnAddOrEditClassroomErrorOverall"
          className="row ps-3 mt-3 pe-3 float-end"
          style={{ color: "#CC0000" }}
        ></span>
      </div>
    </Container>
  );
}
